import React, { memo, useCallback, useEffect, useState } from 'react';
import { NextRouter, useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';
import { AuthActions } from 'store/actions';
import { AuthSelectors } from 'store/selectors';
import MagicLinkLoginView, { MagicLinkLoginViewProps } from './MagicLinkLoginView';

export type MagicLinkLoginViewContainerProps = Omit<
  MagicLinkLoginViewProps,
  'isSending' | 'isSuccessfullySubmitted' | 'submittedEmail' | 'onSubmit'
> & {
  redirectTo?: string | null;
  // Partner-supplied domain (JPA-8229/JPA-8191) — only ever set by the `/embedded-login` path
  // (`EmbeddedAuthContainer`); `AuthModal`'s regular web login never passes it, so `sendMagicLink`'s
  // `params` there is unaffected.
  domain?: string;
};

const getRedirectTo = (router: NextRouter): string => {
  const { pathname, asPath, query } = router;
  const { redirect_to } = query;

  if (typeof redirect_to === 'string') return redirect_to;
  if (pathname !== '/') return asPath;
  return null;
};

const MagicLinkLoginViewContainer: React.FC<React.PropsWithChildren<MagicLinkLoginViewContainerProps>> = (props) => {
  const { redirectTo, domain, ...rest } = props;
  const router = useRouter();
  const dispatch = useDispatch();
  const sendMagicLinkState = useSelector(AuthSelectors.sendMagicLinkState);
  const [submittedEmail, setSubmittedEmail] = useState('');

  const isSuccessfullySubmitted = sendMagicLinkState.isFulfilled && !sendMagicLinkState.error;
  const isSending = sendMagicLinkState.isPending;
  const redirect_to = redirectTo !== undefined ? redirectTo : getRedirectTo(router);

  const handleSubmit = useCallback(
    (values: any) => {
      setSubmittedEmail(values.email);
      dispatch(
        AuthActions.sendMagicLink.request({
          params: {
            ...values,
            ...(redirect_to && {
              redirect_to,
            }),
            ...(domain && { domain }),
          },
        }),
      );
    },
    [dispatch, redirect_to, domain],
  );

  useEffect(() => {
    if (sendMagicLinkState.isFulfilled) {
      return () => {
        dispatch(AuthActions.sendMagicLink.reset());
      };
    }
  }, [dispatch, sendMagicLinkState.isFulfilled]);

  return (
    <MagicLinkLoginView
      {...rest}
      isSending={isSending}
      isSuccessfullySubmitted={isSuccessfullySubmitted}
      submittedEmail={submittedEmail}
      onSubmit={handleSubmit}
    />
  );
};

export default memo(MagicLinkLoginViewContainer);
